import { useState, useEffect } from 'react'

import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, rectIntersection, DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import _ from 'lodash'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'

import { useLevelPlaySurface } from '@/shared/level-play'
import { FormControl } from '@/shared/ui/Form'

import { SortedGameBoard } from './Board'
import { SortedGameGhostWord } from './Word'

export const LevelPlaySorted = ({ activity }) => {
  const { isSubmitting, onComplete } = useLevelPlaySurface()
  const { meta } = activity
  const correctWords = _.get(meta, 'correct_words', '').split(';')
  const wrongWords = _.get(meta, 'wrong_words', '').split(';')
  const ignoreOrder = _.get(meta, 'ignore_order', false)

  const { setValue } = useFormContext()
  const allWords = _.shuffle(_.concat(correctWords, wrongWords).filter(n => n))
  const [items, setItems] = useState({
    correctWords: [],
    wrongWords: allWords
  })
  const [activeId, setActiveId] = useState(null)
  const [containers, setContainers] = useState(Object.keys(items))
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const findContainer = id => {
    if (id in items) return id

    return Object.keys(items).find(key => items[key].includes(id))
  }
  const handleDragStart = event => {
    const { active } = event
    const { id } = active

    setActiveId(id)
  }
  const handleDragOver = ({ active, over }) => {
    const overId = over?.id

    if (overId == null || active.id in items) return
    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id)

    if (!overContainer || !activeContainer) return

    if (activeContainer !== overContainer) {
      setItems(items => {
        const activeItems = items[activeContainer]
        const overItems = items[overContainer]
        const overIndex = overItems.indexOf(overId)
        const activeIndex = activeItems.indexOf(active.id)

        let newIndex

        if (overId in items) {
          newIndex = overItems.length + 1
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height

          const modifier = isBelowOverItem ? 1 : 0

          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1
        }

        return {
          ...items,
          [activeContainer]: items[activeContainer].filter(
            item => item !== active.id
          ),
          [overContainer]: [
            ...items[overContainer].slice(0, newIndex),
            items[activeContainer][activeIndex],
            ...items[overContainer].slice(newIndex, items[overContainer].length)
          ]
        }
      })
    }
  }
  const handleDragEnd = ({ active, over }) => {
    if (active.id in items && over?.id) {
      setContainers(containers => {
        const activeIndex = containers.indexOf(active.id)
        const overIndex = containers.indexOf(over.id)

        return arrayMove(containers, activeIndex, overIndex)
      })
    }

    const activeContainer = findContainer(active.id)

    if (!activeContainer) {
      setActiveId(null)
      return
    }

    const overId = over?.id

    if (overId == null) {
      setActiveId(null)
      return
    }
    const overContainer = findContainer(overId)

    if (overContainer) {
      const activeIndex = items[activeContainer].indexOf(active.id)
      const overIndex = items[overContainer].indexOf(overId)

      if (activeIndex !== overIndex) {
        setItems(items => {
          const newItems = {
            ...items,
            [overContainer]: arrayMove(
              items[overContainer],
              activeIndex,
              overIndex
            )
          }
          return newItems
        })
      }
    }

    setActiveId(null)
  }

  useEffect(() => {
    setValue('answer', items.correctWords)
  }, [items.correctWords])

  return (
    <div className="flex flex-col items-center justify-center gap-8 h-full">
      <FormControl name="answer" hidden />
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {containers.map(containerId => (
          <SortedGameBoard
            key={containerId}
            id={containerId}
            items={items[containerId]}
            ignoreOrder={ignoreOrder}
          />
        ))}
        {createPortal(
          <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
            <div className={{ 'touch-action': 'none' }}>
              {activeId ? <SortedGameGhostWord id={activeId} /> : null}
            </div>
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  )
}

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
}
